import { dayKeyOf, weekdayOf, type DayKey } from '../lib/time'
import type { Block, Category } from './types'

/**
 * Blocks created by tapping share an exact boundary, so any real distance
 * between two implementation blocks is untracked time — and untracked time
 * interrupts a stretch just as surely as a meeting does. A minute of slack
 * absorbs manual entries snapped to different five-minute marks.
 *
 * The brief defines a stretch as impl blocks "with no non-impl block between
 * them"; treating a gap as a break too is a deliberate reading, because
 * otherwise a two-hour lunch left untracked would join the morning to the
 * afternoon and flatter the number this whole app exists to measure.
 */
export const GAP_TOLERANCE_MS = 60_000

export type Categories = Map<string, Category>

/**
 * A scope is a flag id, or null for everything tracked. Nothing is built in:
 * flag some categories "dev" and the fragmentation of dev time is what you get.
 */
export type Scope = string | null

export function inScope(block: Block, categories: Categories, scope: Scope): boolean {
  const category = categories.get(block.categoryId)
  if (!category) return false
  return scope === null || category.flags.includes(scope)
}

function durationOf(block: Block, now: number): number {
  return Math.max(0, (block.end ?? now) - block.start)
}

export interface ImplStretch {
  start: number
  end: number
  /** The block that broke the stretch, when a tracked one did. */
  interruptedBy: string | null
}

/** Runs of uninterrupted in-scope time — the core of the fragmentation metric. */
export function implStretches(
  blocks: Block[],
  categories: Categories,
  scope: Scope = null,
  now: number = Date.now(),
): ImplStretch[] {
  const sorted = [...blocks].sort((a, b) => a.start - b.start)
  const stretches: ImplStretch[] = []
  let current: ImplStretch | null = null

  for (const block of sorted) {
    const end = block.end ?? now
    if (inScope(block, categories, scope)) {
      if (current && block.start - current.end <= GAP_TOLERANCE_MS) {
        current.end = end
      } else {
        if (current) stretches.push(current)
        current = { start: block.start, end, interruptedBy: null }
      }
      continue
    }
    if (current) {
      current.interruptedBy = block.categoryId
      stretches.push(current)
      current = null
    }
  }
  if (current) stretches.push(current)
  return stretches
}

export function median(values: number[]): number {
  if (values.length === 0) return 0
  const sorted = [...values].sort((a, b) => a - b)
  const middle = Math.floor(sorted.length / 2)
  if (sorted.length % 2 === 1) return sorted[middle] as number
  return ((sorted[middle - 1] as number) + (sorted[middle] as number)) / 2
}

const MINUTE = 60_000
export const FRAGMENTATION_BUCKETS: Array<{ label: string; from: number; to: number }> = [
  { label: '< 15m', from: 0, to: 15 * MINUTE },
  { label: '15–30m', from: 15 * MINUTE, to: 30 * MINUTE },
  { label: '30–60m', from: 30 * MINUTE, to: 60 * MINUTE },
  { label: '1–2h', from: 60 * MINUTE, to: 120 * MINUTE },
  { label: '2h+', from: 120 * MINUTE, to: Number.POSITIVE_INFINITY },
]

export interface Fragmentation {
  durations: number[]
  median: number
  longest: number
  total: number
  buckets: Array<{ label: string; count: number }>
}

export function fragmentation(
  blocks: Block[],
  categories: Categories,
  scope: Scope = null,
  now: number = Date.now(),
): Fragmentation {
  const durations = implStretches(blocks, categories, scope, now).map((s) => s.end - s.start)
  return {
    durations,
    median: median(durations),
    longest: durations.reduce((max, value) => Math.max(max, value), 0),
    total: durations.reduce((sum, value) => sum + value, 0),
    buckets: FRAGMENTATION_BUCKETS.map((bucket) => ({
      label: bucket.label,
      count: durations.filter((value) => value >= bucket.from && value < bucket.to).length,
    })),
  }
}

export interface FlagSlice {
  flagId: string | null
  ms: number
  share: number
}

/**
 * Tracked time split by the flags on the categories that produced it. A category
 * with several flags counts towards each of them, so the slices can add up to
 * more than the total; the share is against the total, not the sum of slices.
 */
export function timeByFlag(
  blocks: Block[],
  categories: Categories,
  now: number = Date.now(),
): { total: number; slices: FlagSlice[] } {
  let total = 0
  const byFlag = new Map<string | null, number>()

  for (const block of blocks) {
    const category = categories.get(block.categoryId)
    if (!category) continue
    const ms = durationOf(block, now)
    total += ms
    if (category.flags.length === 0) {
      byFlag.set(null, (byFlag.get(null) ?? 0) + ms)
      continue
    }
    for (const flagId of category.flags) {
      byFlag.set(flagId, (byFlag.get(flagId) ?? 0) + ms)
    }
  }

  const slices = [...byFlag.entries()]
    .map(([flagId, ms]) => ({ flagId, ms, share: total > 0 ? ms / total : 0 }))
    .sort((a, b) => b.ms - a.ms)
  return { total, slices }
}

/** What most often ends a run of in-scope time. */
export function interruptionSources(
  blocks: Block[],
  categories: Categories,
  scope: Scope = null,
  now: number = Date.now(),
): Array<{ categoryId: string; count: number }> {
  const counts = new Map<string, number>()
  for (const stretch of implStretches(blocks, categories, scope, now)) {
    if (!stretch.interruptedBy) continue
    counts.set(stretch.interruptedBy, (counts.get(stretch.interruptedBy) ?? 0) + 1)
  }
  return [...counts.entries()]
    .map(([categoryId, count]) => ({ categoryId, count }))
    .sort((a, b) => b.count - a.count)
}

export function categoryBreakdown(
  blocks: Block[],
  categories: Categories,
  now: number = Date.now(),
): Array<{ categoryId: string; ms: number }> {
  const totals = new Map<string, number>()
  for (const block of blocks) {
    if (!categories.has(block.categoryId)) continue
    totals.set(block.categoryId, (totals.get(block.categoryId) ?? 0) + durationOf(block, now))
  }
  return [...totals.entries()]
    .map(([categoryId, ms]) => ({ categoryId, ms }))
    .sort((a, b) => b.ms - a.ms)
}

/**
 * Categories left out of every figure the app computes.
 *
 * The category says so itself and nothing else does. A flag could once say it
 * for a whole group, which meant two places to look and two to keep in step;
 * `normaliseTaxonomy` carries that old answer onto the categories on read.
 */
export function excludedCategories(categories: Categories): Set<string> {
  const ids = new Set<string>()
  for (const [id, category] of categories) {
    if (category.excludeFromInsights) ids.add(id)
  }
  return ids
}

/** The blocks every figure is computed from. */
export function countedBlocks(blocks: Block[], excluded: Set<string>): Block[] {
  if (excluded.size === 0) return blocks
  return blocks.filter((block) => !excluded.has(block.categoryId))
}

export interface DayBalance {
  day: DayKey
  counted: number
  target: number
}

export interface WorkBalance {
  /** Counted time on days that are over. */
  counted: number
  /** What those days asked for. */
  target: number
  /** counted − target. Negative is time owed, positive is time banked. */
  balance: number
  days: DayBalance[]
  /** Today so far, kept out of the balance because the day is not over. */
  today: number
  todayTarget: number
  ratio: number
  /** Below this the other numbers are not worth trusting. */
  trustworthy: boolean
}

export const COVERAGE_FLOOR = 0.7

/**
 * How the days compare to what they were meant to hold.
 *
 * Deliberately not "did you fill nine to six". Arriving early or staying late
 * counts, an hour of lunch flagged out of Insights does not, and a seven-hour
 * day followed by a nine-hour one nets to zero — because the balance is
 * cumulative rather than a verdict per day.
 *
 * Today is left out of it. A day still in progress would otherwise read as
 * hours owed all morning and settle only at the end, which says nothing.
 */
export function workBalance(
  blocks: Block[],
  days: DayKey[],
  settings: { dayStartHour: number; dailyTargetHours: number },
  today: DayKey,
  now: number = Date.now(),
): WorkBalance {
  const targetMs = Math.max(0, settings.dailyTargetHours) * 3600_000

  // Nothing is expected before the first thing was ever tracked: an app used for
  // one afternoon has not missed the three weeks before you installed it.
  const earliest = blocks.reduce(
    (first, block) => Math.min(first, block.start),
    Number.POSITIVE_INFINITY,
  )
  const firstTrackedDay = Number.isFinite(earliest)
    ? dayKeyOf(new Date(earliest), settings.dayStartHour)
    : null

  const byDay = new Map<DayKey, number>()
  for (const block of blocks) {
    const day = dayKeyOf(new Date(block.start), settings.dayStartHour)
    byDay.set(day, (byDay.get(day) ?? 0) + Math.max(0, (block.end ?? now) - block.start))
  }

  const balanceDays: DayBalance[] = []
  for (const day of days) {
    if (firstTrackedDay === null || day < firstTrackedDay || day > today) continue
    // A weekend asks for nothing. It is not tied to whether the Week view shows
    // one — that is about what you look at, not what you owe — and time actually
    // worked on a Saturday still counts, so it banks hours rather than being
    // ignored.
    const off = weekdayOf(day) >= 5
    const target = off ? 0 : targetMs
    const counted = byDay.get(day) ?? 0
    if (off && counted === 0) continue
    if (day === today) continue
    balanceDays.push({ day, counted, target })
  }

  const counted = balanceDays.reduce((sum, entry) => sum + entry.counted, 0)
  const target = balanceDays.reduce((sum, entry) => sum + entry.target, 0)
  const ratio = target > 0 ? counted / target : counted > 0 ? 1 : 0
  const todayOff = weekdayOf(today) >= 5

  return {
    counted,
    target,
    balance: counted - target,
    days: balanceDays,
    today: byDay.get(today) ?? 0,
    todayTarget: todayOff ? 0 : targetMs,
    ratio,
    trustworthy: target === 0 || ratio >= COVERAGE_FLOOR,
  }
}

/** Narrow a block list to one tag before any of the above. */
export function filterBlocks(blocks: Block[], filter: { tag?: string | null }): Block[] {
  if (!filter.tag) return blocks
  return blocks.filter((block) => block.tags.includes(filter.tag as string))
}
